/**
 * BSS B2B pricing integration for Horizon.
 *
 * 1. Price sync. BSS hides every element marked with its price attributes until
 *    it has written the customer's price, then flags it with
 *    `bss-b2b-product-active`. It only looks for new elements on load and on
 *    window scroll, but Horizon re-renders prices without scrolling (variant
 *    changes, mega menu, cart drawer, predictive search, quick add). When that
 *    happens we fire the `scroll` event BSS already listens to.
 *
 * 2. `win.ArdyssB2B.unitPrice(variantId, fallbackCents)` returns the B2B
 *    price BSS calculated for a variant (or the fallback), so theme scripts such
 *    as the add to cart total can use it. `ardyss:b2b-prices-updated` fires on
 *    document whenever BSS has (re)written prices.
 *
 * 3. Seal Subscriptions, for customers with a distributor (B2B) price:
 *    - Seal rewrites the product page price on every purchase option change
 *      (`sealsubs:price_update`), fighting BSS and leaving list prices behind.
 *      That event is stopped before it reaches Seal, so the B2B price stays put.
 *    - `data-ardyss-distributor` is set on <html> so the theme hides Seal's
 *      one-time price (see snippets/bss-b2b-integration.liquid).
 */
(() => {
  const PENDING = '[bss-b2b-product-id]:not([bss-b2b-product-active])';
  const ACTIVE_ATTR = 'bss-b2b-product-active';
  const UPDATED_EVENT = 'ardyss:b2b-prices-updated';
  /** BSS attaches untyped globals to window. */
  const win = /** @type {any} */ (window);

  /* ---------------- B2B price lookup ---------------- */

  /**
   * @param {number | string | null | undefined} variantId
   * @returns {number | null} B2B price in cents, or null when no B2B price applies.
   */
  const b2bPrice = (variantId) => {
    const storage = win.BSS_B2B?.storage?.variantStorage;
    if (!variantId || !storage?.get) return null;
    const variant = storage.get(Number(variantId)) ?? storage.get(String(variantId));
    const price = variant?.bssB2BPriceAfterPL ?? variant?.bssB2BPriceAfterCP;
    return typeof price === 'number' && Number.isFinite(price) ? Math.round(price) : null;
  };

  win.ArdyssB2B = {
    /**
     * @param {number | string | null | undefined} variantId
     * @param {number} fallbackCents
     */
    unitPrice: (variantId, fallbackCents) => b2bPrice(variantId) ?? fallbackCents,
    b2bPrice,
  };

  /* ---------------- 1. Price sync ---------------- */

  let queued = false;
  const notifyBss = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      if (document.querySelector(PENDING)) window.dispatchEvent(new Event('scroll'));
    });
  };

  let updateTimer = 0;
  const announcePricesUpdated = () => {
    clearTimeout(updateTimer);
    updateTimer = window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent(UPDATED_EVENT));
    }, 60);
  };

  /** @param {Node} node */
  const hasPending = (node) =>
    node instanceof Element && (node.matches(PENDING) || node.querySelector(PENDING) !== null);

  const observer = new MutationObserver((mutations) => {
    let pending = false;
    let processed = false;

    for (const mutation of mutations) {
      const target = mutation.target;
      if (mutation.type === 'attributes') {
        if (!(target instanceof Element)) continue;
        // A morph that drops BSS's "processed" flag leaves the price hidden again.
        if (target.hasAttribute(ACTIVE_ATTR)) processed = true;
        else pending = true;
        continue;
      }

      // BSS rewrote a price it had already processed (e.g. after a variant change).
      if (target instanceof Element && target.closest(`[${ACTIVE_ATTR}]`)) processed = true;

      for (const node of mutation.addedNodes) {
        if (hasPending(node)) pending = true;
      }
    }

    if (pending) notifyBss();
    if (processed) announcePricesUpdated();
  });

  /* ---------------- 3. Seal Subscriptions ---------------- */

  /** @param {Element | null | undefined} [scope] */
  const productVariantId = (scope) => {
    const form =
      scope?.closest('form[action*="/cart/add"]') ||
      scope?.querySelector?.('form[action*="/cart/add"]') ||
      document.querySelector('product-form-component form[action*="/cart/add"]');
    const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('[name="id"]'));
    return input?.value || null;
  };

  const isDistributor = () =>
    document.documentElement.hasAttribute('data-customer-is-distributor') ||
    b2bPrice(productVariantId()) != null;

  const syncDistributorFlag = () => {
    document.documentElement.toggleAttribute('data-ardyss-distributor', isDistributor());
  };

  // Capture on window runs before Seal's listener on document.
  window.addEventListener(
    'sealsubs:price_update',
    (event) => {
      const element = /** @type {CustomEvent} */ (event).detail?.element;
      if (b2bPrice(productVariantId(element)) != null) event.stopImmediatePropagation();
    },
    true
  );

  /* ---------------- Boot ---------------- */

  const start = () => {
    if (typeof win.BSS_B2B === 'undefined') return;
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: false,
      attributes: true,
      attributeFilter: [ACTIVE_ATTR, 'bss-b2b-variant-id'],
    });

    document.addEventListener(UPDATED_EVENT, syncDistributorFlag);
    document.addEventListener('variant:update', syncDistributorFlag);

    // On load BSS only processes prices near the main content (cards, mega menu and
    // footer wait for the first scroll). BSS registers its scroll listener asynchronously,
    // so nudge it a few times after load until nothing is pending.
    const nudge = (delays) => {
      if (!delays.length) return;
      setTimeout(() => {
        if (document.querySelector(PENDING)) notifyBss();
        announcePricesUpdated();
        nudge(delays.slice(1));
      }, delays[0]);
    };
    const onLoad = () => nudge([800, 1500, 3000]);
    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad, { once: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
